/*
 * GridSearch — the ONE shared list-search box (CLAUDE.md §18.6 / §7.5).
 * Every list form's search row uses the same `.list-search-row` / `.list-search`
 * markup (theme/boStandard.css) — previously hand-copied into ~40 pages. This
 * component is the single source of that markup so a new form drops it in
 * instead of re-typing it, and any future tweak (icon, clear button, layout)
 * lands everywhere at once.
 *
 *  Usage:
 *    <GridSearch value={search} onChange={setSearch} />
 *    <GridSearch value={search} onChange={setSearch} placeholder="Search across all columns…" />
 *    <GridSearch value={search} onChange={setSearch} right={<span>3 of 10 matched</span>} />
 *    <GridSearch variant="toolbar" value={search} onChange={setSearch} placeholder="Text to search..." />
 *
 *  `variant="toolbar"` (opt-in, 2026-11-03) is the G5 toolbar find box —
 *  "Search [Text to search... 🔍]" — for a form whose legacy bar carries Search
 *  AFTER Export to: put it in the <AppToolbar> `right` slot. Every other form
 *  keeps the default search row, unchanged. First used by Reagent Preparation.
 */
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Extra content on the same row, right of the search box (e.g. a match count). Row variant only. */
  right?: ReactNode;
  /** Extra inline style on the `.list-search-row` (e.g. maximized-mode padding). Row variant only. */
  style?: CSSProperties;
  /** 'row' (default) = the search row above a grid; 'toolbar' = the inline G5 find box. */
  variant?: 'row' | 'toolbar';
  /** Toolbar variant: the caption before the box (default "Search"). */
  label?: string;
  /** Toolbar variant, OPT-IN: apply the search only on Enter or the 🔍 button, as
   *  XAF's toolbar Search (FullTextFilterAction) does — typing alone changes nothing.
   *  Clearing (✕ / Esc) applies at once. Unset = filters as you type (unchanged).
   *  First used by LLT Search / LLT Depletion (2026-11-03). */
  applyOnEnter?: boolean;
  /** Toolbar variant, OPT-IN: tooltip on the box — e.g. XAF's "Filter records by
   *  text" (G5 parity, 2026-11-03). Unset = no tooltip (unchanged). */
  title?: string;
}

export function GridSearch({ value, onChange, placeholder = 'Search…', right, style, variant = 'row', label = 'Search', applyOnEnter = false, title }: Props) {
  const id = useId();
  // applyOnEnter: what is typed, not yet applied. Follows `value` when the page changes it.
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (variant === 'toolbar') {
    const text = applyOnEnter ? draft : value;
    const type = (v: string) => (applyOnEnter ? setDraft(v) : onChange(v));
    const clear = () => { setDraft(''); onChange(''); };
    return (
      <span className="grid-search-tb" data-unsaved-ignore>
        <label htmlFor={id}>{label}</label>
        <span className="grid-search-tb__box">
          <input id={id} type="text" placeholder={placeholder} value={text} title={title}
                 onChange={(e) => type(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && applyOnEnter) { e.preventDefault(); onChange(draft); }
                   // Esc clears a typed search first (and stops there); an empty box lets Esc through.
                   else if (e.key === 'Escape' && text) { e.stopPropagation(); clear(); }
                 }} />
          {text && (
            <button type="button" className="grid-search-tb__clear" aria-label="Clear search" onClick={clear}>×</button>
          )}
          {applyOnEnter ? (
            <button type="button" className="grid-search-tb__go" aria-label="Search" title="Search (Enter)"
                    onClick={() => onChange(draft)}><i className="ti ti-search" aria-hidden="true" /></button>
          ) : (
            <i className="ti ti-search" aria-hidden="true" />
          )}
        </span>
      </span>
    );
  }
  return (
    <div className="list-search-row" style={style}>
      <div className="list-search">
        <i className="ti ti-search" />
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {value && (
          <span className="list-search-clear" onClick={() => onChange('')}>×</span>
        )}
      </div>
      {right}
    </div>
  );
}
